import { combineEpics, ofType } from 'redux-observable';
import { map, switchMap, catchError } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { asyncError, asyncFinish } from '../../common/+store/index';
import {
  GET_ACCELERATED_TESTERS,
  UPDATE_ACCELERATED_TESTER_OVERRIDE,
  UPDATE_ACCELERATED_TESTER_OVERRIDES_BULK,
  UPLOAD_ACCELERATED_TESTERS_FILE,
} from './acceleratedTesters.actions';
import {
  getAcceleratedTesters,
  updateAcceleratedTesterOverride,
  updateAcceleratedTesterOverridesBulk,
  uploadAcceleratedTestersFile,
} from './acceleratedTesters.api';
import { commonApiCallFragment } from '../../common/+store/utils/utils.api';

const getAcceleratedTestersEpic = (action$) =>
  action$.pipe(
    ofType(GET_ACCELERATED_TESTERS),
    switchMap((action) =>
      from(getAcceleratedTesters(action.snapshotYear)).pipe(
        commonApiCallFragment(action$, action, 'Get Accelerated Testers')
      )
    ),
    switchMap((result) => {
      if (result.success) {
        return of(
          asyncFinish(result.action.type, {
            acceleratedTesters: result.response,
          })
        );
      }
      if (result.actions) return of(...result.actions);
      return of(result.action);
    })
  );

const updateAcceleratedTesterOverrideEpic = (action$) =>
  action$.pipe(
    ofType(UPDATE_ACCELERATED_TESTER_OVERRIDE),
    switchMap((action) =>
      from(updateAcceleratedTesterOverride(action.snapshotYear, action.firebaseId, action.override)).pipe(
        map((data) => ({ action, success: true, data })),
        catchError((error) =>
          of({
            success: false,
            action: asyncError(action.type, 'Could not update override', error),
          })
        )
      )
    ),
    switchMap((results) => {
      if (results.success) {
        return of(
          asyncFinish(results.action.type, {
            firebaseId: results.action.firebaseId,
            override: results.action.override,
          })
        );
      }
      if (results.actions) return of(...results.actions);
      return of(results.action);
    })
  );

const updateAcceleratedTesterOverridesBulkEpic = (action$) =>
  action$.pipe(
    ofType(UPDATE_ACCELERATED_TESTER_OVERRIDES_BULK),
    switchMap((action) =>
      from(updateAcceleratedTesterOverridesBulk(action.snapshotYear, action.updates)).pipe(
        map((data) => ({ action, success: true, data })),
        catchError((error) =>
          of({
            success: false,
            action: asyncError(action.type, 'Could not bulk update overrides', error),
          })
        )
      )
    ),
    switchMap((results) => {
      if (results.success) {
        return of(asyncFinish(results.action.type, { updates: results.action.updates }));
      }
      if (results.actions) return of(...results.actions);
      return of(results.action);
    })
  );

const uploadAcceleratedTestersFileEpic = (action$) =>
  action$.pipe(
    ofType(UPLOAD_ACCELERATED_TESTERS_FILE),
    switchMap((action) =>
      from(uploadAcceleratedTestersFile(action.profile, action.file, action.snapshotYear)).pipe(
        map((result) => ({ action, success: true, result })),
        catchError((error) =>
          of({
            success: false,
            action,
            // Surface the api's clear message (e.g. wrong file type) to the toast.
            error: (error && error.message) || 'Could not upload the Accelerated Testers file.',
            rawError: error,
          })
        )
      )
    ),
    switchMap((results) => {
      if (results.success) {
        // Refresh the node in place (no asyncStart -> no full-page spinner) so the
        // newly imported students appear, and signal success for the toast.
        return of(asyncFinish(results.action.type, { result: results.result }), {
          type: GET_ACCELERATED_TESTERS,
          snapshotYear: results.action.snapshotYear,
        });
      }
      return of(asyncError(results.action.type, results.error, results.rawError));
    })
  );

const epics = combineEpics(
  getAcceleratedTestersEpic,
  updateAcceleratedTesterOverrideEpic,
  updateAcceleratedTesterOverridesBulkEpic,
  uploadAcceleratedTestersFileEpic
);
export default epics;
